/* AUTO-SYNCED from PsyKit.io/components/layout/NavShell.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
import Link from "next/link";
import { Brandmark } from "@/components/brand/Brandmark";
import { Wordmark } from "@/components/brand/Wordmark";

/**
 * Shared top-bar chrome for BOTH the public marketing nav and the authed
 * dashboard header. Owns the translucent-navy bar, blur, bottom border, and the
 * brand lockup (Ψ + Psy180 → "/") so the two bars stay visually identical while
 * keeping their own link logic. Change the bar once here; both navs update.
 *
 * - `position`: "sticky" for marketing pages (flows with scroll); "fixed" for
 *   the dashboard (the (app) layout already offsets its content with pt-14).
 * - `contained`: wrap the row in the 1180px marketing container (true) or run
 *   full-bleed like the dashboard (false).
 *
 * The inner row is `relative` so the mobile menu panel can position itself with
 * `absolute top-full` directly under the bar.
 */
export function NavShell({
  children,
  position = "sticky",
  contained = true,
  className = "",
}: {
  children: React.ReactNode;
  position?: "sticky" | "fixed";
  contained?: boolean;
  className?: string;
}) {
  const pin = position === "fixed" ? "fixed top-0 left-0 right-0" : "sticky top-0";

  return (
    <header
      // Inline z-index is deliberate: it beats ANY stylesheet rule (including the
      // unlayered `header {}` in globals.css and Tailwind's layered utilities), so
      // the bar — and its absolutely-positioned mobile menu — can never get trapped
      // below page content again, regardless of cascade-layer ordering or a stale
      // CSS chunk. The `z-50` class stays for intent; this enforces it.
      style={{ zIndex: 50 }}
      className={`${pin} z-50 border-b border-[var(--color-border-strong)] bg-[rgba(6,15,24,0.92)] shadow-[0_6px_24px_-12px_rgba(0,0,0,0.7)] backdrop-blur-[14px] ${className}`}
    >
      <div
        // Fixed height (h-14 = 3.5rem) so the (app) layout's pt-14 offset matches
        // the bar EXACTLY — content can never be clipped under or gap below it.
        className={`relative flex h-14 items-center justify-between gap-6 px-7 ${
          contained ? "mx-auto max-w-[1180px]" : ""
        }`}
      >
        <Link
          href="/"
          className="flex shrink-0 items-center gap-3"
          aria-label="Psy180 home"
        >
          <Brandmark size={36} className="shrink-0" />
          <Wordmark className="text-[22px]" />
        </Link>
        {children}
      </div>
    </header>
  );
}
